import { useUploadFileMutation } from "@/hooks/mutations/useUploadFileMutation";
import { useUploadStyleIdsFromUrlMutation } from "@/hooks/mutations/useUploadStyleIdsFromUrlMutation";
import { useUploadStyleIdsMutation } from "@/hooks/mutations/useUploadStyleIdsMutation";
import { cn } from "@/lib/utils";
import { DataSource, UploadedSessionData } from "@/types/common.types";
import { logAndAlertError } from "@/utils/loggers";
import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { InputFile, InputUrl } from "./InputForUpload";
import { CustomAlert } from "../ui/Alert";
import Text from "../common/Text";
import { Label } from "../ui/label";
import { useForm } from "react-hook-form";
import { useDateRangeFilter } from "../ui/Filter/useDateRangeFilter";
import {
  JobStyleScheduleVariables,
  useJobStyleScheduleMutation,
} from "@/hooks/mutations/useJobStyleScheduleMutation";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { DatePicker } from "../ui/DatePicker";
import { CalendarPlus, Check } from "lucide-react";
import { useGetRolesQuery, useGetTenantsQuery } from "@/hooks/queries";
import { motion, AnimatePresence } from "framer-motion";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";

const scrapingFrequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

type UploadFormProps = {
  onUploadSuccess: (
    uploadSessionId: UploadedSessionData,
    jobName: string
  ) => void;
  onScheduleSuccess?: (data: JobStyleScheduleVariables) => void;
  className?: string;
};

const TabButton = ({
  isActive,
  onClick,
  children,
}: {
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium leading-[14px] transition-colors"
  >
    <div
      className={cn(
        "flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] border-shades-neutral-200",
        isActive && "border-brand-accent"
      )}
    >
      {isActive && <div className="h-2 w-2 rounded-full bg-brand-accent" />}
    </div>
    <span className="text-shades-neutral-300">{children}</span>
  </button>
);

const UploadAndScheduleForm = ({
  onUploadSuccess,
  onScheduleSuccess,
}: UploadFormProps) => {
  const { data: roleData } = useGetRolesQuery();

  const { register, formState, watch } = useForm<{ jobName: string }>();
  const [activeTab, setActiveTab] = useState<"file" | "url" | "styleId">(
    "file"
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [url, setUrl] = useState("");
  const [styleId, setStyleId] = useState("");
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);

  const [isScheduled, setIsScheduled] = useState(false);
  const [scrapingFrequency, setScrapingFrequency] = useState<string>("");
  const { sendEvent } = useAnalyticsManager();
  const { mutate, isPending } = useJobStyleScheduleMutation();

  const {
    dateRange,
    handleFromDateChange: handleFromDateChangeString,
    handleToDateChange: handleToDateChangeString,
  } = useDateRangeFilter();

  const { data: tenants } = useGetTenantsQuery({
    enabled: roleData?.isSuperAdmin,
  });

  const toggleTenant = (tenantId: string) => {
    setSelectedTenants((prev) =>
      prev.includes(tenantId)
        ? prev.filter((id) => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const onStyleIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStyleId(event.target.value);
  };
  const clearInputs = () => {
    setSelectedFile(null);
    setUrl("");
    setStyleId("");
  };

  const uploadFileMutation = useUploadFileMutation({
    onSuccess: (data) => {
      sendEvent("file_upload_success", {
        upload_mode: "file",
        file_format: selectedFile?.name.split(".").pop() || "",
        file_size_mb: selectedFile?.size ? selectedFile.size / 1024 / 1024 : 0,
      });
      onUploadSuccess(data, watch("jobName"));
      if (isScheduleValid()) {
        handleSchedule(data);
      }
    },
    onError: (error) => {
      sendEvent("file_upload_failure", {
        error_type: error?.message || "Unknown error",
      });
      logAndAlertError({
        userInfo: "Upload file failed",
        devInfo: error,
      });
    },
  });

  const handleFromDateChange = (date: Date | undefined) => {
    if (!date) {
      handleFromDateChangeString(undefined);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      CustomAlert.error({
        description: "Start date cannot be before today",
        position: "top-right",
      });
      return;
    }

    if (dateRange?.toDate && new Date(dateRange.toDate) < date) {
      CustomAlert.error({
        description: "Start date cannot be after end date",
        position: "top-right",
      });
      return;
    }

    handleFromDateChangeString(date.toISOString());
    if (date && dateRange?.fromDate) {
      sendEvent("upload_schedule_dates_selected", {
        start_date: dateRange.fromDate,
        end_date: date.toISOString(),
      });
    }
  };

  const handleToDateChange = (date: Date | undefined) => {
    if (!date) {
      handleToDateChangeString(undefined);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      CustomAlert.error({
        description: "End date cannot be before today",
        position: "top-right",
      });
      return;
    }

    if (dateRange?.fromDate && date < new Date(dateRange.fromDate)) {
      CustomAlert.error({
        description: "End date cannot be before start date",
        position: "top-right",
      });
      return;
    }

    handleToDateChangeString(date.toISOString());
  };

  const handleFrequencyChange = (value: string) => {
    setScrapingFrequency(value);
    sendEvent("upload_frequency_selected", {
      frequency: value,
    });
  };

  const isScheduleValid = () => {
    if (roleData?.isSuperAdmin) {
      return (
        scrapingFrequency &&
        dateRange?.fromDate &&
        dateRange?.toDate &&
        selectedTenants.length > 0
      );
    }
    return scrapingFrequency && dateRange?.fromDate && dateRange?.toDate;
  };

  const handleSchedule = (uploadedData: UploadedSessionData) => {
    const frequencyMap = {
      daily: 24,
      weekly: 24 * 7,
      monthly: 24 * 30,
    };
    if (selectedTenants.length === 0 && roleData?.isSuperAdmin) {
      return CustomAlert.error({
        description: "Please select at least one tenant",
        position: "top-right",
      });
    }

    mutate(
      {
        jobName: watch("jobName"),
        uploadSessionId: uploadedData.id,
        styleIds: uploadedData.styleIds?.map((d) => ({
          styleId: d.styleId,
          dataSource: d.dataSource,
        })),
        // Send ISO strings, never raw epoch millis: the backend deserializes a
        // bare number as epoch SECONDS, which turned a missing end date into a
        // year-58377 endTime. Open-ended schedules default to end of 2035.
        startTime: dateRange?.fromDate ?? new Date().toISOString(),
        endTime: dateRange?.toDate ?? "2035-12-31T18:30:00.000Z",
        frequency:
          frequencyMap[scrapingFrequency as keyof typeof frequencyMap] || 24,
        tenantIds: selectedTenants,
      },
      {
        onSuccess(_, variables) {
          CustomAlert.success({
            description: "Job scheduled successfully",
            position: "top-right",
          });
          setIsScheduled(true);

          onScheduleSuccess?.(variables);
        },
        onError(error) {
          logAndAlertError({
            userInfo: "Couldn't schedule job",
            devInfo: error,
          });
        },
      }
    );
  };

  const uploadStyleIdsFromUrlMutation = useUploadStyleIdsFromUrlMutation({
    onSuccess: (data) => {
      onUploadSuccess(data, watch("jobName"));
      if (isScheduleValid()) {
        handleSchedule(data);
      }
      clearInputs();
    },
    onError: (error) => {
      logAndAlertError({
        userInfo: "Upload style ids from url failed",
        devInfo: error,
      });
    },
  });

  const uploadStyleIdsMutation = useUploadStyleIdsMutation({
    onSuccess: (data) => {
      onUploadSuccess(data, watch("jobName"));
      if (isScheduleValid()) {
        handleSchedule(data);
      }
      clearInputs();
    },
    onError: (error) => {
      logAndAlertError({
        userInfo: "Upload style ids failed",
        devInfo: error,
      });
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setUrl("");
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    setSelectedFile(null);
  };

  const handleFileUpload = (selectedFile: File, jobName: string) => {
    if (!selectedFile) throw new Error("No file selected");
    sendEvent("file_upload_clicked", {
      upload_mode: "file",
      file_format: selectedFile.type,
      file_size_mb: selectedFile.size / 1024 / 1024,
    });
    uploadFileMutation.mutate({
      files: [selectedFile],
      jobName,
      dataSource: DataSource.Myntra,
    });
  };

  const handleUploadStyleIdsFromUrl = (jobName: string) => {
    uploadStyleIdsFromUrlMutation.mutate({
      url,
      jobName,
      dataSource: DataSource.Myntra,
    });
  };

  const handleUploadStyleIds = (jobName: string) => {
    uploadStyleIdsMutation.mutate({
      styleIds: [styleId],
      jobName,
      dataSource: DataSource.Myntra,
    });
  };

  const noInput = !selectedFile && !url && !styleId;

  const handleUpload = () => {
    if (!watch("jobName")) {
      return CustomAlert.error({
        description: "Please add a job title first",
        position: "top-left",
      });
    }
    if (noInput) {
      return CustomAlert.error({
        description: "Please add at least one source of Style Ids",
        position: "top-left",
      });
    }
    if (selectedFile) {
      handleFileUpload(selectedFile, watch("jobName"));
    } else if (url) {
      handleUploadStyleIdsFromUrl(watch("jobName"));
    } else if (styleId) {
      handleUploadStyleIds(watch("jobName"));
    }
    sendEvent("upload_submit_clicked", {
      job_title: watch("jobName"),
      upload_mode: activeTab,
      file_name: selectedFile?.name.split(".").shift() || url || styleId,
      frequency_value: scrapingFrequency,
      start_date: dateRange?.fromDate || "",
      end_date: dateRange?.toDate || "",
    });
  };

  return (
    <div className="flex w-full gap-10">
      <div className="flex w-full flex-col items-start gap-6">
        <Text
          variant="heading_h6"
          className="text-[18px] font-medium leading-[31.5px] text-shades-neutral-500"
        >
          Add Product
        </Text>
        <div className="flex w-full flex-col gap-2">
          <Label
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted"
            htmlFor="email"
          >
            Job Title
          </Label>
          <Input
            className={cn(
              "h-12 w-full p-4",
              formState.errors.jobName && "border-brand-accent"
            )}
            type="text"
            id="jobName"
            required
            {...register("jobName", {
              required: "Please enter your job title",
              onChange: (e) => {
                sendEvent("upload_job_title_entered", {
                  job_title: e.target.value,
                });
              },
            })}
            placeholder="Enter job title"
          />
        </div>
        <div className="flex w-full flex-col gap-2">
          <Label
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted"
            htmlFor="email"
          >
            Add File
          </Label>

          <div className="flex flex-row gap-3">
            <TabButton
              isActive={activeTab === "file"}
              onClick={() => {
                setActiveTab("file");
                sendEvent("upload_mode_selected", {
                  mode: "file",
                });
              }}
            >
              Upload
            </TabButton>
            <TabButton
              isActive={activeTab === "url"}
              onClick={() => {
                setActiveTab("url");
                sendEvent("upload_mode_selected", {
                  mode: "url",
                });
              }}
            >
              URL
            </TabButton>
            <TabButton
              isActive={activeTab === "styleId"}
              onClick={() => {
                setActiveTab("styleId");
                sendEvent("upload_mode_selected", {
                  mode: "styleId",
                });
              }}
            >
              Single Style ID
            </TabButton>
          </div>

          <div className="mt-4">
            {activeTab === "file" && (
              <InputFile
                onFileChange={handleFileChange}
                isUploading={uploadFileMutation.isPending}
                selectedFile={selectedFile}
                disabled={!!url || !!styleId}
                onClearFile={handleClearFile}
              />
            )}
            {activeTab === "url" && (
              <InputUrl
                value={url}
                onChange={handleUrlChange}
                disabled={!!selectedFile || !!styleId}
              />
            )}
            {activeTab === "styleId" && (
              <InputUrl
                value={styleId}
                onChange={onStyleIdChange}
                disabled={!!selectedFile || !!url}
                placeholder="Enter Single Style ID"
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col items-start gap-6">
        <Text
          variant="heading_h6"
          className="text-[18px] font-medium leading-[31.5px] text-shades-neutral-500"
        >
          Schedule Frequency
        </Text>

        <div className="flex w-full flex-col gap-2">
          <Label
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted"
            htmlFor="jobFrequency"
          >
            Frequency
          </Label>
          <Select
            value={scrapingFrequency}
            onValueChange={handleFrequencyChange}
          >
            <SelectTrigger
              className="h-[48px] w-full rounded-none border-brand-border text-brand-text"
              showControlIcon={true}
            >
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {scrapingFrequencyOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-full gap-3">
          <div className="flex flex-1 flex-col gap-2">
            <Label
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted"
              htmlFor="jobFromDate"
            >
              Start Date
            </Label>{" "}
            <DatePicker
              label="From"
              onDateChange={handleFromDateChange}
              date={
                dateRange?.fromDate ? new Date(dateRange.fromDate) : undefined
              }
              calendarProps={{
                toDate: dateRange?.toDate
                  ? new Date(dateRange.toDate)
                  : undefined,
                fromDate: new Date(),
              }}
              className="h-[48px] w-full"
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <Label
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted"
              htmlFor="jobToDate"
            >
              End Date
            </Label>{" "}
            <DatePicker
              label="To"
              onDateChange={handleToDateChange}
              date={dateRange?.toDate ? new Date(dateRange.toDate) : undefined}
              calendarProps={{
                fromDate: dateRange?.fromDate
                  ? new Date(dateRange.fromDate)
                  : new Date(),
              }}
              className="h-[48px] w-full"
            />
          </div>
        </div>

        {/* Pill for tenants */}
        {roleData?.isSuperAdmin && (
          <div className="flex w-full flex-col gap-2">
            <Label
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted"
              htmlFor="jobFromDate"
            >
              Tenants
            </Label>
            <motion.div
              className="flex flex-wrap gap-3 overflow-visible"
              layout
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
                mass: 0.5,
              }}
            >
              {tenants?.map((tenant) => {
                const isSelected = selectedTenants.includes(
                  tenant.id.toString()
                );
                return (
                  <motion.button
                    key={tenant.id}
                    onClick={() => toggleTenant(tenant.id.toString())}
                    layout
                    initial={false}
                    animate={{
                      backgroundColor: isSelected ? "#E8ECFB" : "transparent",
                    }}
                    whileHover={{
                      backgroundColor: isSelected ? "#E8ECFB" : "#E8ECFB",
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                      mass: 0.5,
                      backgroundColor: { duration: 0.1 },
                    }}
                    className={cn(
                      "inline-flex items-center overflow-hidden whitespace-nowrap rounded-full px-4 py-2 text-base font-medium text-zinc-400 ring-1 ring-inset ring-shades-neutral-200",
                      isSelected && "text-brand-accent ring-brand-accent"
                    )}
                  >
                    <motion.div
                      className="relative flex items-center"
                      animate={{
                        width: isSelected ? "auto" : "100%",
                        paddingRight: isSelected ? "1.5rem" : "0",
                      }}
                      transition={{
                        ease: [0.175, 0.885, 0.32, 1.275],
                        duration: 0.3,
                      }}
                    >
                      <span>{tenant.name}</span>
                      <AnimatePresence>
                        {isSelected && (
                          <motion.span
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                              mass: 0.5,
                            }}
                            className="absolute right-0"
                          >
                            <div className="flex h-4 w-4 items-center justify-center rounded-full bg-brand-accent">
                              <Check
                                className="h-3 w-3 text-white"
                                strokeWidth={1.5}
                              />
                            </div>
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </motion.button>
                );
              })}
            </motion.div>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleUpload}
            isLoading={
              uploadFileMutation.isPending ||
              uploadStyleIdsFromUrlMutation.isPending ||
              uploadStyleIdsMutation.isPending ||
              isPending
            }
            className="disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!isScheduleValid() || isScheduled}
            leftIcon={<CalendarPlus color="white" height={20} width={20} />}
          >
            Submit & Schedule
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadAndScheduleForm;
